import React, { useEffect, useId, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CameraIcon,
  CheckBadgeIcon,
  DocumentArrowUpIcon,
  QrCodeIcon,
  ShareIcon,
  SparklesIcon,
  TrophyIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const DEMO_LINES = [
  'HKS GTII turbo kit',
  'BC Racing coilovers',
  'AeroCatch hood pins',
  'StopTech BBK',
  'Custom titanium exhaust',
  'Volk TE37 SL 18x9.5',
];

const CATEGORIES = [
  { name: 'Power', count: 2, color: 'bg-orange-500/80' },
  { name: 'Suspension', count: 1, color: 'bg-orange-400/70' },
  { name: 'Brakes', count: 1, color: 'bg-white/40' },
  { name: 'Exterior', count: 2, color: 'bg-white/25' },
];

const AWARDS = [
  { label: 'Best Engine', event: 'Mountain Meet' },
  { label: "People's Choice", event: 'Night Cruise' },
  { label: 'Cleanest Bay', event: 'Club Show' },
];

const ALBUMS = [
  { id: 'bay', label: 'Engine bay', src: '/images/brand/workshop-mod-detail.jpg' },
  { id: 'track', label: 'Track day', src: '/images/brand/paddock-meet-dusk.jpg' },
  { id: 'build', label: 'Build shots', src: '/images/brand/home-story-workshop.jpg' },
];

const CHAPTERS = [
  { year: '2022', text: 'Base car + first coilover set' },
  { year: '2024', text: 'Turbo kit and tune logged' },
  { year: '2026', text: 'Club badge + show award' },
];

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

/** Interactive demo: paste-style smart import that categorizes sample parts. */
function SmartImportWidget() {
  const [parsed, setParsed] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const reduced = usePrefersReducedMotion();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearInterval(timerRef.current);
    };
  }, []);

  const runParse = () => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (parsed) {
      setParsed(false);
      setVisibleCount(0);
      return;
    }
    setParsed(true);
    if (reduced) {
      setVisibleCount(DEMO_LINES.length);
      return;
    }
    setVisibleCount(0);
    let n = 0;
    timerRef.current = window.setInterval(() => {
      n += 1;
      setVisibleCount(n);
      if (n >= DEMO_LINES.length && timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 90);
  };

  return (
    <article className="md:col-span-7 md:row-span-2 rounded-2xl border border-white/[0.08] bg-[#0c0c0c] overflow-hidden flex flex-col group isolate">
      <div className="relative min-h-[11rem] sm:min-h-[13rem] overflow-hidden bg-[#0c0c0c]">
        <img
          src="/images/brand/home-smart-import.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover scale-105 transition-transform duration-700 ease-out group-hover:scale-110 will-change-transform"
          loading="lazy"
          decoding="async"
        />
        <div
          className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/75 to-[#0c0c0c]/30"
          aria-hidden
        />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-[#0a0a0a]/80 px-3 py-1 text-xs text-orange-300 backdrop-blur-sm">
          <SparklesIcon className="h-3.5 w-3.5" aria-hidden strokeWidth={1.5} />
          Smart import
        </div>
      </div>

      <div className="relative z-[1] -mt-px px-5 sm:px-6 py-5 flex-1 flex flex-col bg-[#0c0c0c]">
        <h3 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight mb-2">
          Drop a list. Keep the structure.
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed max-w-[44ch] mb-4">
          Paste a spreadsheet, PDF, or chat dump. Bilt groups parts by category and brand before
          anything hits the build.
        </p>

        <div className="rounded-xl border border-white/[0.08] bg-[#0a0a0a]/90 p-3 sm:p-4 flex-1">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">
              {parsed ? 'Review before save' : 'Sample parts list'}
            </p>
            <button
              type="button"
              onClick={runParse}
              className="inline-flex items-center gap-1.5 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-medium text-orange-300 hover:bg-orange-500/20 active:scale-[0.98] transition-colors min-h-[36px] cursor-pointer"
            >
              {parsed ? (
                <>
                  <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden strokeWidth={1.5} />
                  Reset
                </>
              ) : (
                <>
                  <DocumentArrowUpIcon className="h-3.5 w-3.5" aria-hidden strokeWidth={1.5} />
                  Parse demo
                </>
              )}
            </button>
          </div>

          {!parsed ? (
            <ul className="space-y-1.5 font-mono text-[12px] text-gray-400">
              {DEMO_LINES.map((line) => (
                <li key={line} className="truncate border-b border-white/[0.04] pb-1 last:border-0">
                  {line}
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ul className="space-y-1.5">
                {DEMO_LINES.slice(0, visibleCount).map((line) => (
                  <li
                    key={line}
                    className="flex items-center gap-2 text-xs text-gray-200 animate-fade-in"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0" aria-hidden />
                    <span className="truncate">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-2">
                {CATEGORIES.map((cat, i) => (
                  <div
                    key={cat.name}
                    className="flex items-center gap-2"
                    style={reduced ? undefined : { opacity: visibleCount > i * 1.2 ? 1 : 0.35 }}
                  >
                    <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className={`h-full ${cat.color} rounded-full transition-all duration-500`}
                        style={{ width: parsed ? `${30 + cat.count * 22}%` : '0%' }}
                      />
                    </div>
                    <span className="font-mono text-[10px] text-gray-400 w-20 text-right">
                      {cat.name} {cat.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/** Doc score ring that animates in on first view / click. */
function DocScoreWidget() {
  const [score, setScore] = useState(42);
  const reduced = usePrefersReducedMotion();
  const gid = useId();
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;

  const bump = () => {
    setScore((s) => (s >= 92 ? 42 : Math.min(92, s + 12)));
  };

  useEffect(() => {
    if (reduced) {
      setScore(78);
      return;
    }
    const t = window.setTimeout(() => setScore(78), 400);
    return () => window.clearTimeout(t);
  }, [reduced]);

  return (
    <article className="md:col-span-5 rounded-2xl border border-white/[0.08] bg-[#111] p-5 sm:p-6 flex flex-col min-h-[14rem]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-2">
            <CheckBadgeIcon className="h-4 w-4" aria-hidden strokeWidth={1.5} />
            Documentation score
          </p>
          <h3 className="font-display text-lg sm:text-xl font-semibold text-white tracking-tight mb-2">
            See what is still thin
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            Completeness for photos, specs, and history. Not a vanity metric.
          </p>
        </div>
        <button
          type="button"
          onClick={bump}
          className="relative shrink-0 h-[5.5rem] w-[5.5rem] rounded-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 active:scale-[0.98] transition-transform"
          aria-label={`Documentation score ${score}. Click to preview filling gaps.`}
        >
          <svg viewBox="0 0 96 96" className="h-full w-full -rotate-90" aria-hidden>
            <circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
            <circle
              cx="48"
              cy="48"
              r={r}
              fill="none"
              stroke={`url(#${gid})`}
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={offset}
              className="transition-[stroke-dashoffset] duration-700 ease-out"
            />
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#fb923c" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>
          </svg>
          <span className="absolute inset-0 flex items-center justify-center font-display text-xl font-semibold text-white tabular-nums">
            {score}
          </span>
        </button>
      </div>
      <p className="mt-4 text-xs text-gray-500">Click the score to preview filling gaps.</p>
    </article>
  );
}

function MediaAlbumsWidget() {
  const [active, setActive] = useState(ALBUMS[0].id);
  const current = ALBUMS.find((a) => a.id === active) ?? ALBUMS[0];

  return (
    <article className="md:col-span-5 rounded-2xl border border-white/[0.08] bg-[#0c0c0c] overflow-hidden relative min-h-[14rem] flex flex-col">
      <div className="relative flex-1 min-h-[9rem]">
        {ALBUMS.map((album) => (
          <img
            key={album.id}
            src={album.src}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
              album.id === active ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            decoding="async"
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/45 to-transparent" aria-hidden />
      </div>
      <div className="relative px-5 py-4 -mt-16 z-[1]">
        <p className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-2">
          <CameraIcon className="h-4 w-4" aria-hidden strokeWidth={1.5} />
          Media albums
        </p>
        <h3 className="font-display text-lg font-semibold text-white tracking-tight mb-3">
          {current.label} stays with the car
        </h3>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Album types">
          {ALBUMS.map((album) => (
            <button
              key={album.id}
              type="button"
              role="tab"
              aria-selected={album.id === active}
              onClick={() => setActive(album.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium min-h-[36px] transition-colors active:scale-[0.98] cursor-pointer ${
                album.id === active
                  ? 'bg-orange-500 text-white'
                  : 'bg-white/[0.06] text-gray-300 hover:bg-white/[0.1]'
              }`}
            >
              {album.label}
            </button>
          ))}
        </div>
      </div>
    </article>
  );
}

function AwardsWidget() {
  const [idx, setIdx] = useState(0);
  const award = AWARDS[idx];

  return (
    <article className="md:col-span-4 rounded-2xl border border-white/[0.08] bg-[#111] p-5 sm:p-6 flex flex-col justify-between min-h-[12rem]">
      <div>
        <p className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-3">
          <TrophyIcon className="h-4 w-4" aria-hidden strokeWidth={1.5} />
          Events and awards
        </p>
        <h3 className="font-display text-lg font-semibold text-white tracking-tight mb-2">
          Recognition on the profile
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed">
          Register from your garage. Judging and trophies attach to the same build.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setIdx((i) => (i + 1) % AWARDS.length)}
        className="mt-5 w-full rounded-xl border border-white/[0.08] bg-[#0a0a0a]/80 px-4 py-3 text-left hover:border-orange-500/30 transition-colors active:scale-[0.99] cursor-pointer"
        aria-label={`Show next award. Current: ${award.label}`}
      >
        <p className="font-mono text-[10px] uppercase tracking-wider text-gray-500">{award.event}</p>
        <p className="font-display text-orange-400 font-semibold mt-0.5">{award.label}</p>
        <p className="text-[11px] text-gray-500 mt-1">Tap to cycle awards</p>
      </button>
    </article>
  );
}

function TimelineWidget() {
  const [active, setActive] = useState(1);

  return (
    <article className="md:col-span-4 rounded-2xl border border-white/[0.08] overflow-hidden relative min-h-[12rem] bg-[#0c0c0c]">
      <img
        src="/images/brand/home-event-chapter-wide.jpg"
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-40"
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0 bg-[#0a0a0a]/75" aria-hidden />
      <div className="relative h-full p-5 flex flex-col">
        <h3 className="font-display text-lg font-semibold text-white tracking-tight mb-1">
          Chapters over captions
        </h3>
        <p className="text-gray-300 text-sm leading-relaxed mb-4">What changed, when, and why.</p>
        <ol className="space-y-2 flex-1">
          {CHAPTERS.map((ch, i) => (
            <li key={ch.year}>
              <button
                type="button"
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left rounded-lg px-3 py-2 border transition-colors cursor-pointer ${
                  active === i
                    ? 'border-orange-500/40 bg-orange-500/10'
                    : 'border-transparent bg-white/[0.03] hover:bg-white/[0.06]'
                }`}
              >
                <span className="font-mono text-[10px] text-orange-400/90">{ch.year}</span>
                <span className="block text-sm text-gray-200 mt-0.5">{ch.text}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </article>
  );
}

function QrShareWidget() {
  const [flipped, setFlipped] = useState(false);

  return (
    <article className="md:col-span-4 rounded-2xl border border-white/[0.08] bg-gradient-to-br from-orange-500/[0.12] to-[#0c0c0c] overflow-hidden min-h-[12rem] flex flex-col">
      <div className="relative h-28 overflow-hidden">
        <img
          src="/images/brand/home-qr-windshield.jpg"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] to-transparent" aria-hidden />
      </div>
      <div className="px-5 py-4 flex-1 flex flex-col">
        <p className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-2">
          <ShareIcon className="h-4 w-4" aria-hidden strokeWidth={1.5} />
          Public page + QR
        </p>
        <h3 className="font-display text-lg font-semibold text-white tracking-tight mb-2">
          One link for the paddock
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed mb-4">
          Print a sticker for the windshield. Judges and shops scan straight to the build.
        </p>
        <button
          type="button"
          onClick={() => setFlipped((f) => !f)}
          className="mt-auto self-start inline-flex items-center gap-3 rounded-xl border border-white/[0.1] bg-[#0a0a0a]/80 px-3 py-2.5 hover:border-orange-500/35 transition-colors active:scale-[0.98] cursor-pointer"
          aria-pressed={flipped}
          aria-label={flipped ? 'Hide sample QR' : 'Show sample QR'}
        >
          <span
            className={`grid grid-cols-5 gap-0.5 p-1.5 rounded bg-white transition-transform duration-300 ${
              flipped ? 'scale-100' : 'scale-90 opacity-80'
            }`}
            aria-hidden
          >
            {Array.from({ length: 25 }, (_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 ${
                  [0, 1, 2, 4, 5, 6, 8, 10, 12, 14, 16, 18, 20, 21, 22, 24].includes(i) ||
                  (flipped && i % 3 === 0)
                    ? 'bg-[#0a0a0a]'
                    : 'bg-transparent'
                }`}
              />
            ))}
          </span>
          <span className="text-sm text-gray-200">
            <QrCodeIcon className="h-4 w-4 text-orange-400 inline mr-1.5 align-text-bottom" aria-hidden strokeWidth={1.5} />
            {flipped ? 'Sample QR ready' : 'Preview QR'}
          </span>
        </button>
      </div>
    </article>
  );
}

function ShopsWidget() {
  const [credited, setCredited] = useState(false);

  return (
    <article className="md:col-span-6 rounded-2xl border border-white/[0.08] bg-[#0c0c0c] p-5 sm:p-6 min-h-[11rem] flex flex-col sm:flex-row gap-5">
      <div className="flex-1">
        <p className="inline-flex items-center gap-2 text-orange-400 text-sm font-medium mb-2">
          <WrenchScrewdriverIcon className="h-4 w-4" aria-hidden strokeWidth={1.5} />
          Shop credit
        </p>
        <h3 className="font-display text-lg font-semibold text-white tracking-tight mb-2">
          Credit the shop that built it
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed">
          Tag verified shops on the build. Their work shows next to the parts they installed.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setCredited((c) => !c)}
        className={`sm:w-52 shrink-0 rounded-xl border px-4 py-3 text-left transition-all active:scale-[0.98] cursor-pointer ${
          credited
            ? 'border-orange-500/40 bg-orange-500/10'
            : 'border-white/[0.08] bg-[#111] hover:border-white/[0.14]'
        }`}
        aria-pressed={credited}
      >
        <p className="font-display text-sm font-semibold text-white">Ridge Line Fabrication</p>
        <p className="text-xs text-gray-400 mt-1">Exhaust · fabrication</p>
        <p className={`text-xs mt-3 font-medium ${credited ? 'text-orange-300' : 'text-gray-500'}`}>
          {credited ? 'Credited on build' : 'Tap to credit'}
        </p>
      </button>
    </article>
  );
}

function DynoWidget() {
  const [mode, setMode] = useState<'wheel' | 'crank'>('wheel');

  return (
    <article className="md:col-span-6 rounded-2xl border border-white/[0.08] bg-[#111] p-5 sm:p-6 min-h-[11rem] flex flex-col justify-between">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-500 mb-2">
            Dyno history
          </p>
          <h3 className="font-display text-lg font-semibold text-white tracking-tight mb-1">
            Dyno sheets on the build
          </h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            Log wheel or crank figures with shop or owner verification. Sessions stay with the car.
          </p>
        </div>
        <div
          className="flex rounded-lg border border-white/[0.08] p-0.5 shrink-0"
          role="group"
          aria-label="Measurement location"
        >
          {(['wheel', 'crank'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-2.5 py-1.5 text-xs rounded-md min-h-[32px] transition-colors cursor-pointer ${
                mode === m ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              {m === 'wheel' ? 'Wheel' : 'Crank'}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 flex items-end justify-between gap-3">
        <p className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-orange-400 tabular-nums">
          {mode === 'wheel' ? '412' : '468'}
          <span className="text-lg sm:text-xl text-gray-400 font-medium ml-2">
            {mode === 'wheel' ? 'whp' : 'chp'}
          </span>
        </p>
        <span className="mb-1 inline-flex items-center rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
          Verified · shop
        </span>
      </div>
    </article>
  );
}

/**
 * Interactive feature mosaic for the public home.
 * Pink Slips–cued widgets, Bilt features and orange accent.
 */
export const HomeFeatureMosaic: React.FC = () => (
  <section className="relative py-20 sm:py-28 overflow-hidden" aria-labelledby="feature-mosaic-heading">
    <div
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(249,115,22,0.08),transparent_50%)]"
      aria-hidden
    />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center mb-12 sm:mb-14">
        <h2
          id="feature-mosaic-heading"
          className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white text-balance"
        >
          More than a mod list.
        </h2>
        <p className="text-gray-400 text-base sm:text-lg mt-4 leading-relaxed max-w-[46ch] mx-auto">
          Import the parts, fill the gaps, share a QR, and keep awards on the same page as the car.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4">
        <SmartImportWidget />
        <DocScoreWidget />
        <MediaAlbumsWidget />
        <AwardsWidget />
        <TimelineWidget />
        <QrShareWidget />
        <ShopsWidget />
        <DynoWidget />
      </div>
    </div>
  </section>
);

export default HomeFeatureMosaic;
